import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, Sparkle, Spinner, Storefront, EyeSlash, CheckSquare } from "@phosphor-icons/react";
import { PUB_FILTERS } from "./publisherShared";

export function PublisherStats({ stats, filter, setFilter }) {
  const cards = [
    { key: "all", label: "Con stock", value: stats.total, cls: "text-zinc-900" },
    { key: "published", label: "Publicados", value: stats.published, cls: "text-emerald-700" },
    { key: "ready", label: "Listos sin publicar", value: stats.ready_unpublished, cls: "text-[#002FA7]" },
    { key: "no_photo", label: "Sin foto", value: stats.no_photo, cls: "text-amber-700" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pub-stats">
      {cards.map((c) => (
        <button type="button" key={c.key} onClick={() => setFilter(c.key)}
          className={`text-left rounded-lg border px-3 py-2.5 bg-white transition-colors hover:border-[#002FA7]/50 ${filter === c.key ? "border-[#002FA7] ring-1 ring-[#002FA7]/30" : "border-zinc-200"}`}
          data-testid={`pub-stat-${c.key}`}>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{c.label}</div>
          <div className={`font-heading text-2xl font-extrabold ${c.cls}`}>{c.value}</div>
        </button>
      ))}
    </div>
  );
}

export function PublisherToolbar({ q, setQ, cat, setCat, categories, filter, setFilter, counts }) {
  return (
    <div className="space-y-2" data-testid="pub-toolbar">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 min-w-0">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, marca, SKU…" className="pl-9" data-testid="pub-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-full sm:w-56" data-testid="pub-category"><SelectValue placeholder="Categoría" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5 [&>*]:shrink-0" data-testid="pub-filters">
        {PUB_FILTERS.map((f) => (
          <button type="button" key={f.key} onClick={() => setFilter(f.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-200 hover:border-[#002FA7]/50"}`}
            data-testid={`pub-filter-${f.key}`}>
            {f.label} <span className={`font-mono-data ${filter === f.key ? "text-white/80" : "text-zinc-400"}`}>{counts[f.key] ?? 0}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function PublisherActions({ visible, selected, allChecked, toggleAll, selectReady, readyCount, canUseAlendra, running, progress, publishing, onComplete, onPublish, onUnpublish }) {
  const n = selected.size;
  return (
    <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border border-zinc-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2" data-testid="pub-actions">
      <label className="inline-flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
        <input type="checkbox" className="h-4 w-4 accent-[#002FA7]" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} disabled={visible.length === 0} data-testid="pub-check-all" />
        {n > 0 ? <b data-testid="pub-selected-count">{n} seleccionado{n === 1 ? "" : "s"}</b> : <span>Seleccionar visibles ({visible.length})</span>}
      </label>
      {readyCount > 0 && (
        <button type="button" onClick={selectReady} className="text-xs text-[#002FA7] underline inline-flex items-center gap-1" data-testid="pub-select-ready"><CheckSquare size={13} /> Seleccionar listos ({readyCount})</button>
      )}
      <div className="flex-1" />
      {running && <span className="text-xs text-[#7E57C2] font-mono-data inline-flex items-center gap-1" data-testid="pub-ai-progress"><Spinner size={13} className="animate-spin" /> {progress.done}/{progress.total} completados</span>}
      {canUseAlendra && (
        <Button size="sm" onClick={onComplete} disabled={n === 0 || running || publishing}
          className="h-8 gap-1.5 text-white bg-gradient-to-br from-[#7E57C2] to-[#5e35b1] hover:shadow-md" title="Alendra completa ficha técnica, slogan, usos, badges y prioridad (solo campos vacíos) y guarda" data-testid="pub-complete-ai">
          <Sparkle size={14} weight="fill" /> Completar con Alendra{n > 0 ? ` (${n})` : ""}
        </Button>
      )}
      <Button size="sm" onClick={onPublish} disabled={n === 0 || running || publishing} className="h-8 gap-1.5 bg-[#002FA7] hover:bg-[#002277] text-white" title="Publica en la tienda los seleccionados que estén al 100%" data-testid="pub-publish">
        {publishing ? <Spinner size={14} className="animate-spin" /> : <Storefront size={14} weight="fill" />} Publicar{n > 0 ? ` (${n})` : ""}
      </Button>
      <Button size="sm" variant="outline" onClick={onUnpublish} disabled={n === 0 || running || publishing} className="h-8 gap-1.5" data-testid="pub-unpublish">
        <EyeSlash size={14} /> Despublicar
      </Button>
    </div>
  );
}
